import { useCallback, useEffect, useRef, useState } from "react";
import { getCategories, searchProducts } from "@/api/public.api.js";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";
import { EMPTY_PRODUCT_FILTERS } from "@/utils/productFilters.js";
import HomePage from "./HomePage.jsx";

export default function HomeContainer() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [draftFilters, setDraftFilters] = useState(EMPTY_PRODUCT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_PRODUCT_FILTERS);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const appliedFiltersRef = useRef(appliedFilters);
    appliedFiltersRef.current = appliedFilters;

    const fetchPage = useCallback(async (pageToLoad, filters, append) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError("");
        try {
            const response = await searchProducts(filters, pageToLoad);
            setProducts((prev) => (append ? [...prev, ...(response.data || [])] : response.data || []));
            setHasNext(!!response.hasNext);
            setPage(pageToLoad);
        } catch (err) {
            setError(err.message);
            if (!append) {
                setProducts([]);
                setHasNext(false);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        getCategories().then((response) => setCategories(response.data || [])).catch(() => {});
        fetchPage(0, EMPTY_PRODUCT_FILTERS, false);
    }, [fetchPage]);

    const applyFilters = async () => {
        const nextFilters = { ...draftFilters };
        setAppliedFilters(nextFilters);
        await fetchPage(0, nextFilters, false);
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_PRODUCT_FILTERS);
        setAppliedFilters(EMPTY_PRODUCT_FILTERS);
        await fetchPage(0, EMPTY_PRODUCT_FILTERS, false);
    };

    const loadMore = useCallback(() => {
        fetchPage(page + 1, appliedFiltersRef.current, true);
    }, [fetchPage, page]);

    const sentinelRef = useInfiniteScroll({ hasNext, loading, loadingMore, onLoadMore: loadMore });

    return (
        <HomePage
            products={products}
            categories={categories}
            draftFilters={draftFilters}
            onFiltersChange={setDraftFilters}
            onApplyFilters={applyFilters}
            onResetFilters={resetFilters}
            loading={loading}
            loadingMore={loadingMore}
            sentinelRef={sentinelRef}
            error={error}
        />
    );
}
